import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/api';

/* ═══════════════════════════════════════════════════════════════════
   AURA RING — premium half-circle ring around the profile avatar.

   • TOP half  : silver by default. Becomes GOLD for the 24 h following
                 a day on which the user achieved all 6 pillars fused
                 (i.e. today is gold iff yesterday == last_divine_date).
                 Earning Divine today only sets up tomorrow's gold.
   • BOTTOM half: 6 equal arc segments (Power · Fuel · Force · Energy ·
                 Destiny · Aegis) using the master-card colours.
                 Fused today → bright + glowing. Otherwise → dim.

   USAGE:
     <AuraRing>{avatar}</AuraRing>                 // current user
        → reads aura state from localStorage (instant) + syncs from
          /api/aura/me every 8 s (also pushes local mutations to backend).
     <AuraRing data={{fused, gold}}>{avatar}</AuraRing>   // any user
        → renders from explicit data (passed by Discover / public profile).
   ═══════════════════════════════════════════════════════════════════ */

const AURA_STORAGE_KEY = 'aura_codex_state_v1';
const GOLD_TOP_KEY = 'aura_gold_top_v1';
/* Second gold source: any Pronoia ring action taken TODAY lights the
   profile ring for the rest of today (resets at midnight). */
const PRONOIA_GOLD_TODAY_KEY = 'pronoia_gold_today_v1';

const PILLAR_ORDER = ['power', 'fuel', 'force', 'energy', 'destiny', 'aegis'];
const PILLAR_COLOR = {
  power:   { bright: '#FF6478', dim: 'rgba(255,100,120,0.22)' },
  fuel:    { bright: '#5BE08A', dim: 'rgba(91,224,138,0.22)'  },
  force:   { bright: '#5DD4FF', dim: 'rgba(93,212,255,0.22)'  },
  energy:  { bright: '#B978FF', dim: 'rgba(185,120,255,0.22)' },
  destiny: { bright: '#7C9BFF', dim: 'rgba(124,155,255,0.22)' },
  aegis:   { bright: '#FFB265', dim: 'rgba(230,126,34,0.22)'  },
};

const todayKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const polar = (cx, cy, r, deg) => {
  const rad = (deg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const arcPath = (cx, cy, r, startDeg, endDeg) => {
  const s = polar(cx, cy, r, startDeg);
  const e = polar(cx, cy, r, endDeg);
  const large = (endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
};

const readFusedToday = () => {
  try {
    const raw = localStorage.getItem(AURA_STORAGE_KEY);
    if (!raw) return {};
    const s = JSON.parse(raw);
    if (s?.date !== todayKey()) return {};
    const out = {};
    PILLAR_ORDER.forEach((id) => { out[id] = !!s.pillars?.[id]?.fused; });
    return out;
  } catch (_) { return {}; }
};

/* Today's gold = EITHER (a) persisted achievement date equals yesterday
   (6 auras fused yesterday → gold today), OR (b) the user forged any
   Pronoia ring action today (until midnight, then reverts). */
const readGoldEntitlement = () => {
  try {
    const today = todayKey();

    /* Rule B — Pronoia ring lit today */
    try {
      const pRaw = localStorage.getItem(PRONOIA_GOLD_TODAY_KEY);
      if (pRaw) {
        const p = JSON.parse(pRaw);
        if (p?.date === today) return true;
      }
    } catch (_) { /* noop */ }

    /* Rule A — 6 auras fused yesterday */
    const raw = localStorage.getItem(AURA_STORAGE_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s?.date === today) {
        const allFusedToday = PILLAR_ORDER.every((id) => !!s.pillars?.[id]?.fused);
        if (allFusedToday) {
          const storedRaw = localStorage.getItem(GOLD_TOP_KEY);
          let stored = null;
          try { stored = storedRaw ? JSON.parse(storedRaw) : null; } catch (_) { stored = null; }
          if (!stored || stored.date !== today) {
            localStorage.setItem(GOLD_TOP_KEY, JSON.stringify({ date: today }));
          }
        }
      }
    }
    const storedRaw2 = localStorage.getItem(GOLD_TOP_KEY);
    if (!storedRaw2) return false;
    let stored2 = null;
    try { stored2 = JSON.parse(storedRaw2); } catch (_) { return false; }
    if (!stored2?.date) return false;
    const yest = new Date();
    yest.setDate(yest.getDate() - 1);
    const ym = String(yest.getMonth() + 1).padStart(2, '0');
    const yd = String(yest.getDate()).padStart(2, '0');
    const yesterday = `${yest.getFullYear()}-${ym}-${yd}`;
    return stored2.date === yesterday;
  } catch (_) { return false; }
};

/* Push the local state up to /api/aura/me. Fire-and-forget; failures
   just leave the server slightly behind until the next push. */
let pushTimer = null;
export const schedulePush = () => {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(async () => {
    pushTimer = null;
    try {
      const fused = readFusedToday();
      /* Never push an empty ring — it would wipe today's server copy after a fresh login. */
      if (!Object.values(fused).some(Boolean)) return;
      await axios.post(`${BACKEND_URL}/api/aura/me`, {
        fused: PILLAR_ORDER.reduce((acc, id) => { acc[id] = !!fused[id]; return acc; }, {}),
        date: todayKey(),
        tz_offset: new Date().getTimezoneOffset(),
      });
    } catch (_) { /* ignore */ }
  }, 500);
};

export const AuraRing = ({ size = 124, data = null, children }) => {
  /* If `data` is provided, render from it directly (used by Discover
     and other-user profile views). Otherwise, manage the current user's
     state via localStorage + backend sync. */
  const isCurrentUser = !data;

  const [fused, setFused] = useState(() =>
    data ? data.fused || {} : readFusedToday()
  );
  const [gold, setGold] = useState(() =>
    data ? !!data.gold : readGoldEntitlement()
  );

  /* When `data` changes (Discover re-fetches), re-render. */
  useEffect(() => {
    if (data) {
      setFused(data.fused || {});
      setGold(!!data.gold);
    }
  }, [data]);

  /* Current-user only: refresh from localStorage + push to backend. */
  useEffect(() => {
    if (!isCurrentUser) return undefined;

    let lastSnapshot = JSON.stringify(readFusedToday());

    const refresh = () => {
      const nextFused = readFusedToday();
      const snapshot = JSON.stringify(nextFused);
      setFused(nextFused);
      setGold(readGoldEntitlement());
      // Only push if something changed since the last push.
      if (snapshot !== lastSnapshot) {
        lastSnapshot = snapshot;
        schedulePush();
      }
    };

    refresh();
    /* Fusions made inside the Aura tile happen while this ring is unmounted —
       always sync local fused state up on mount. Otherwise restore from server. */
    if (Object.values(readFusedToday()).some(Boolean)) {
      schedulePush();
    } else {
      axios.get(`${BACKEND_URL}/api/aura/me`).then(({ data: d }) => {
        if (!d?.fused || !Object.values(d.fused).some(Boolean)) return;
        if (Object.values(readFusedToday()).some(Boolean)) return;
        let prev = {};
        try { prev = JSON.parse(localStorage.getItem(AURA_STORAGE_KEY) || '{}'); } catch { prev = {}; }
        const pillars = {};
        PILLAR_ORDER.forEach((id) => {
          const old = prev.pillars?.[id] || {};
          pillars[id] = { tasks: [], window: { start: '', end: '' }, lastFusedAt: 0, ...old, fused: !!d.fused[id] };
        });
        localStorage.setItem(AURA_STORAGE_KEY, JSON.stringify({ ...prev, date: todayKey(), pillars }));
        lastSnapshot = JSON.stringify(readFusedToday());
        refresh();
      }).catch(() => {});
    }
    const t = window.setInterval(refresh, 8000);
    const onStorage = (e) => {
      if (e.key === AURA_STORAGE_KEY || e.key === GOLD_TOP_KEY || e.key === PRONOIA_GOLD_TODAY_KEY) refresh();
    };
    const onVis = () => { if (!document.hidden) refresh(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('aura-fused', refresh);
    window.addEventListener('bliss-cloud-restored', refresh);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      window.removeEventListener('aura-fused', refresh);
      window.removeEventListener('bliss-cloud-restored', refresh);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isCurrentUser]);

  const STROKE = 5;
  const r = (size - STROKE) / 2;
  const c = size / 2;

  const topPath = arcPath(c, c, r, 270, 450);
  const segDeg = 180 / 6;
  const segments = PILLAR_ORDER.map((id, i) => {
    const start = 90 + i * segDeg;
    const end   = 90 + (i + 1) * segDeg;
    return { id, path: arcPath(c, c, r, start, end) };
  });

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size} height={size}
        className="absolute inset-0 pointer-events-none"
        data-testid="aura-ring">
        <defs>
          <linearGradient id="auraRingSilver" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#F8F9FB" />
            <stop offset="48%" stopColor="#C7C9CF" />
            <stop offset="100%" stopColor="#7C7E84" />
          </linearGradient>
          <linearGradient id="auraRingGold" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#FFF1B8" />
            <stop offset="45%" stopColor="#F1D27A" />
            <stop offset="100%" stopColor="#8B6B1F" />
          </linearGradient>
        </defs>

        <path
          d={topPath}
          fill="none"
          stroke={gold ? 'url(#auraRingGold)' : 'url(#auraRingSilver)'}
          strokeWidth={STROKE}
          strokeLinecap="round"
          style={{
            filter: gold
              ? 'drop-shadow(0 0 6px rgba(241,210,122,0.65))'
              : 'drop-shadow(0 0 4px rgba(255,255,255,0.18))',
            transition: 'stroke 0.6s ease, filter 0.6s ease',
          }}
        />

        {segments.map(({ id, path }) => {
          const isFused = !!fused[id];
          const color = isFused ? PILLAR_COLOR[id].bright : PILLAR_COLOR[id].dim;
          return (
            <path
              key={id}
              d={path}
              fill="none"
              stroke={color}
              strokeWidth={STROKE}
              strokeLinecap="round"
              style={{
                filter: isFused
                  ? `drop-shadow(0 0 5px ${PILLAR_COLOR[id].bright})`
                  : 'none',
                transition: 'stroke 0.5s ease, filter 0.5s ease',
              }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center" style={{ padding: STROKE + 3 }}>
        {children}
      </div>
    </div>
  );
};

export default AuraRing;
